import { GeneratedImage, SavedAsset } from '../types';
import { db } from './firebase';
import { 
    collection, 
    doc, 
    setDoc, 
    getDocs, 
    deleteDoc, 
    query, 
    orderBy, 
    where,
    writeBatch,
    onSnapshot,
    serverTimestamp
} from "firebase/firestore";

// --- HISTORY ITEMS (Generated Images) ---

export const saveToHistory = async (item: GeneratedImage, userId?: string) => {
    if (!userId || !db) return item.id; 
    try {
        const docRef = doc(db, 'users', userId, 'history', item.id);
        const cleanItem = JSON.parse(JSON.stringify(item)); 
        await setDoc(docRef, {
            ...cleanItem,
            createdAt: serverTimestamp()
        });
        return item.id;
    } catch (e) {
        console.error("Error saving to history:", e);
        return item.id;
    }
};

export const getHistoryItems = async (userId?: string): Promise<GeneratedImage[]> => {
    if (!userId || !db) return [];
    try {
        const q = query(collection(db, 'users', userId, 'history'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as GeneratedImage));
    } catch (e) {
        console.error("Error fetching history:", e);
        return [];
    }
};

export const subscribeToHistory = (userId: string, callback: (items: GeneratedImage[]) => void) => {
    if (!db || !userId) return () => {};
    
    const q = query(collection(db, 'users', userId, 'history'), orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
        const items = snapshot.docs.map(doc => {
            const data = doc.data();
            return { ...data, id: doc.id } as GeneratedImage;
        });
        callback(items);
    });
};

export const removeFromHistory = async (id: string, userId?: string) => {
    if (!userId || !db) return;
    try {
        await deleteDoc(doc(db, 'users', userId, 'history', id));
    } catch (e) {
        console.error("Error deleting history item:", e);
    }
};

export const bulkRemoveFromHistory = async (ids: string[], userId?: string) => {
    if (!userId || !db) return;
    try {
        const batch = writeBatch(db);
        ids.forEach(id => {
            const docRef = doc(db, 'users', userId, 'history', id);
            batch.delete(docRef);
        });
        await batch.commit();
    } catch (e) {
        console.error("Error bulk deleting:", e);
    }
};

export const toggleFavorite = async (id: string, isFavorite: boolean, userId?: string) => {
    if (!userId || !db) return;
    try {
        const docRef = doc(db, 'users', userId, 'history', id);
        await setDoc(docRef, { isFavorite }, { merge: true });
    } catch (e) {
        console.error("Error toggling favorite:", e);
    }
};

export const bulkToggleFavorite = async (ids: string[], isFavorite: boolean, userId?: string) => {
    if (!userId || !db) return;
    try {
        const batch = writeBatch(db);
        ids.forEach(id => {
            const docRef = doc(db, 'users', userId, 'history', id);
            batch.update(docRef, { isFavorite });
        });
        await batch.commit();
    } catch (e) {
        console.error("Error bulk toggling favorite:", e);
    }
};

// --- ASSETS (Backgrounds & Props) ---

export const saveAsset = async (asset: SavedAsset, userId?: string) => {
    if (!userId || !db) return;
    try {
        const docRef = doc(db, 'users', userId, 'assets', asset.id);
        const cleanAsset = JSON.parse(JSON.stringify(asset));
        await setDoc(docRef, {
            ...cleanAsset,
            createdAt: serverTimestamp()
        });
    } catch (e) {
        console.error("Error saving asset:", e);
    }
};

export const getAssets = async (type: 'background' | 'prop', userId?: string): Promise<SavedAsset[]> => {
    if (!userId || !db) return [];
    try {
        const q = query(
            collection(db, 'users', userId, 'assets'), 
            where('type', '==', type),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as SavedAsset));
    } catch (e) {
        console.error("Error fetching assets:", e);
        return [];
    }
};

export const deleteAsset = async (id: string, userId?: string) => {
    if (!userId || !db) return;
    try {
        await deleteDoc(doc(db, 'users', userId, 'assets', id));
    } catch (e) {
        console.error("Error deleting asset:", e);
    }
};

// --- SYNC UTILS ---

export const syncLocalToCloud = async (userId: string): Promise<number> => {
    // Placeholder for local-to-cloud sync logic
    return 0;
};